// src/utils/datosPDFRecepcion.js
// Ayudante compartido para armar los datos del PDF de una recepcion ya guardada.
// Solo lee. No escribe nada en la base de datos.
import supabase from "../supabaseClient";
import { generarPDFRecepcion } from "./generarPDFRecepcion";
import { fechaLocal } from "./internacional";

// Arma la lista de productos de una recepcion a partir del pedido.
// - Recorre grupos y descarta servicios: no se revisan fisicamente.
// - Si el pedido tiene productos_revisados, toma de ahi lo recibido y la
//   observacion, emparejando por nombre.
// - Si no los tiene (recepciones cerradas por SQL en L2-L5), asume que volvio
//   todo y sin observaciones, que es como se cerraron.
export const construirProductosRecepcion = (orden) => {
  const lista = [];

  (orden?.productos || []).forEach((p) => {
    if (p.es_grupo && Array.isArray(p.productos)) {
      const cantidadGrupo = Number(p.cantidad || 1);

      p.productos.forEach((sub) => {
        if (sub.es_servicio) return;

        const cantidadSub = Number(sub.cantidad || 0);
        const multiplicarSub = sub.multiplicar !== false;
        const cantidadEsperada = multiplicarSub
          ? cantidadSub * cantidadGrupo
          : cantidadSub;

        lista.push({
          nombre: sub.nombre,
          esperado: cantidadEsperada,
          recibido: cantidadEsperada,
          observacion: "",
          producto_id: sub.id,
          proveedor: sub.proveedor || null,
          proveedor_id: sub.proveedor_id || null,
          tipo_origen: sub.proveedor_id ? "proveedor" : "propio",
        });
      });
    } else {
      if (p.es_servicio) return;

      lista.push({
        nombre: p.nombre,
        esperado: p.cantidad,
        recibido: p.cantidad,
        observacion: "",
        producto_id: p.id,
        proveedor: p.proveedor || null,
        proveedor_id: p.proveedor_id || null,
        tipo_origen: p.proveedor_id ? "proveedor" : "propio",
      });
    }
  });

  const guardados = Array.isArray(orden?.productos_revisados)
    ? [...orden.productos_revisados]
    : [];

  return lista.map((p) => {
    const i = guardados.findIndex((g) => g && g.nombre === p.nombre);
    if (i === -1) return p;
    const g = guardados.splice(i, 1)[0];
    return {
      ...p,
      recibido: g.recibido != null ? Number(g.recibido) : p.recibido,
      observacion: g.observacion || "",
    };
  });
};

// Pasa la lista anterior al formato exacto que espera el generador de PDF.
export const productosParaPDF = (lista) =>
  (lista || []).map((p) => ({
    descripcion: p.nombre,
    esperado: p.esperado,
    recibido: p.recibido,
    observacion: p.observacion || "",
  }));

// Descarga el PDF de una recepcion ya guardada, a partir del id del pedido.
export const descargarPDFRecepcion = async (ordenId) => {
  const { data: orden, error } = await supabase
    .from("ordenes_pedido")
    .select("*, clientes(*)")
    .eq("id", ordenId)
    .single();

  if (error || !orden) {
    console.error("Error cargando el pedido para el PDF:", error);
    throw new Error("No se pudo cargar el pedido.");
  }

  // Misma regla que usa Recepcion.js: garantia_devuelta tiene valor 0 por
  // defecto, asi que no basta con mirarla. La marca real de que el pedido paso
  // por recepcion es revisada o productos_revisados.
  const garantiaTotal = Number(orden.garantia || 0);
  const yaPasoPorRecepcion =
    orden.revisada === true || orden.productos_revisados != null;
  const garantiaRetenida =
    yaPasoPorRecepcion && orden.garantia_devuelta != null
      ? Math.max(0, garantiaTotal - Number(orden.garantia_devuelta || 0))
      : 0;

  // Los pagos a proveedores hechos en recepcion ya quedaron guardados dentro de
  // orden.pagos_proveedores, y el generador los lee de ahi. Por eso aqui va
  // lista vacia: si no, saldrian dos veces en la tabla de gastos.
  await generarPDFRecepcion(
    orden,
    orden.clientes,
    productosParaPDF(construirProductosRecepcion(orden)),
    orden.abonos_recepcion || [],
    [],
    { garantiaTotal, garantiaRetenida },
    // Los gastos guardados antes del Bloque Y no traen fecha. Se les pone la del
    // evento, igual que hace Recepcion.js al abrir, para que el PDF salga igual
    // desde los dos sitios.
    (orden.gastos_extras_recepcion || []).map((g) => ({
      ...g,
      fecha: g.fecha || fechaLocal(orden.fecha_evento),
    }))
  );
};